import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const TaskContext = createContext({
  tasks: [],
  loading: false,
  assignedToMe: [],
  assignedByMe: [],
  selectedTask: null,
  taskDetailOpen: false,
  fetchTasks: async () => {},
  createTask: async () => {},
  updateTask: async () => {},
  completeTask: async () => {},
  deleteTask: async () => {},
  addComment: async () => {},
  deleteComment: async () => {},
  addAttachment: async () => {},
  deleteAttachment: async () => {},
  downloadAttachment: async () => {},
  openTaskDetail: (taskId) => {},
  closeTaskDetail: () => {}
});

export const TaskProvider = ({ children }) => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  const fetchInProgressRef = useRef(false);

  // Bana atanan görevler (kendine atananlar dahil)
  const assignedToMe = useMemo(() => {
    if (!user || !user.id) return [];
    const userId = user.id;
    const filtered = tasks.filter(task => {
      // assignedToId veya assignedTo.id ile kontrol et
      const assignedToId = task.assignedToId || task.assignedTo?.id;
      return assignedToId === userId;
    });
    return filtered;
  }, [tasks, user]);

  // Benim atadığım görevler
  const assignedByMe = useMemo(() => {
    if (!user || !user.id) return [];
    const userId = user.id;
    const filtered = tasks.filter(task => {
      // assignedById veya assignedBy.id ile kontrol et
      const assignedById = task.assignedById || task.assignedBy?.id;
      return assignedById === userId;
    });
    return filtered;
  }, [tasks, user]);

  // Görevleri çek
  const fetchTasks = useCallback(async (options = {}) => {
    const silent = options?.silent === true;
    if (fetchInProgressRef.current) {
      return;
    }

    fetchInProgressRef.current = true;
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await api.get('/api/tasks');
      if (response.data.success) {
        setTasks(response.data.data || []);
      }
    } catch (error) {
      console.error('Görevleri yükleme hatası:', error);
    } finally {
      fetchInProgressRef.current = false;
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  // Yeni görev oluştur
  const createTask = useCallback(async (taskData) => {
    try {
      const formData = new FormData();
      formData.append('assignedToId', taskData.assignedToId);
      formData.append('title', taskData.title);
      if (taskData.description) formData.append('description', taskData.description);
      if (taskData.dueDate) formData.append('dueDate', taskData.dueDate);
      if (taskData.isRecurring) {
        formData.append('isRecurring', 'true');
        formData.append('recurrencePattern', JSON.stringify(taskData.recurrencePattern));
      }
      
      if (taskData.files && taskData.files.length > 0) {
        taskData.files.forEach((file, index) => {
          formData.append(`file`, file);
        });
      }

      const response = await api.post('/api/tasks', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        await fetchTasks();
        return response.data.data;
      }
      throw new Error(response.data.message || 'Görev oluşturulamadı');
    } catch (error) {
      console.error('Görev oluşturma hatası:', error);
      throw error;
    }
  }, [fetchTasks]);

  // Görev güncelle
  const updateTask = useCallback(async (taskId, updateData) => {
    try {
      const response = await api.patch(`/api/tasks/${taskId}`, updateData);
      if (response.data.success) {
        await fetchTasks();
        if (selectedTask && selectedTask.id === taskId) {
          setSelectedTask(response.data.data);
        }
        return response.data.data;
      }
      throw new Error(response.data.message || 'Görev güncellenemedi');
    } catch (error) {
      console.error('Görev güncelleme hatası:', error);
      throw error;
    }
  }, [fetchTasks, selectedTask]);

  // Görevi tamamla
  const completeTask = useCallback(async (taskId) => {
    try {
      const response = await api.patch(`/api/tasks/${taskId}/complete`);
      if (response.data.success) {
        await fetchTasks();
        if (selectedTask && selectedTask.id === taskId) {
          setSelectedTask(response.data.data);
        }
        return response.data.data;
      }
      throw new Error(response.data.message || 'Görev tamamlanamadı');
    } catch (error) {
      console.error('Görev tamamlama hatası:', error);
      throw error;
    }
  }, [fetchTasks, selectedTask]);

  // Görevi sil
  const deleteTask = useCallback(async (taskId) => {
    try {
      const response = await api.delete(`/api/tasks/${taskId}`);
      if (response.data.success) {
        await fetchTasks();
        if (selectedTask && selectedTask.id === taskId) {
          closeTaskDetail();
        }
        return true;
      }
      throw new Error(response.data.message || 'Görev silinemedi');
    } catch (error) {
      console.error('Görev silme hatası:', error);
      throw error;
    }
  }, [fetchTasks, selectedTask]);

  // Yorum ekle
  const addComment = useCallback(async (taskId, comment) => {
    try {
      const response = await api.post(`/api/tasks/${taskId}/comments`, { comment });
      if (response.data.success) {
        await fetchTasks();
        // Seçili görevi güncelle
        if (selectedTask && selectedTask.id === taskId) {
          const taskResponse = await api.get(`/api/tasks/${taskId}`);
          if (taskResponse.data.success) {
            setSelectedTask(taskResponse.data.data);
          }
        }
        return response.data.data;
      }
      throw new Error(response.data.message || 'Yorum eklenemedi');
    } catch (error) {
      console.error('Yorum ekleme hatası:', error);
      throw error;
    }
  }, [fetchTasks, selectedTask]);

  // Yorum sil
  const deleteComment = useCallback(async (taskId, commentId) => {
    try {
      const response = await api.delete(`/api/tasks/${taskId}/comments/${commentId}`);
      if (response.data.success) {
        await fetchTasks();
        // Seçili görevi güncelle
        if (selectedTask && selectedTask.id === taskId) {
          const taskResponse = await api.get(`/api/tasks/${taskId}`);
          if (taskResponse.data.success) {
            setSelectedTask(taskResponse.data.data);
          }
        }
        return true;
      }
      throw new Error(response.data.message || 'Yorum silinemedi');
    } catch (error) {
      console.error('Yorum silme hatası:', error);
      throw error;
    }
  }, [fetchTasks, selectedTask]);

  // Dosya ekle
  const addAttachment = useCallback(async (taskId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      console.log('Dosya ekleme başlatılıyor:', { taskId, fileName: file.name });

      const response = await api.post(`/api/tasks/${taskId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Dosya ekleme response:', response.data);

      if (response.data.success) {
        // Önce görevleri güncelle
        await fetchTasks();
        
        // Seçili görevi güncelle - response'dan gelen task bilgisini kullan
        if (selectedTask && selectedTask.id === taskId) {
          // Response'da task bilgisi varsa onu kullan
          if (response.data.data?.task?.attachments) {
            const updatedTask = {
              ...selectedTask,
              attachments: response.data.data.task.attachments
            };
            console.log('Task güncelleniyor (response\'dan):', updatedTask);
            setSelectedTask(updatedTask);
          } else {
            // Yoksa API'den tekrar çek
            const taskResponse = await api.get(`/api/tasks/${taskId}`);
            if (taskResponse.data.success) {
              console.log('Task güncelleniyor (API\'den):', taskResponse.data.data);
              setSelectedTask(taskResponse.data.data);
            }
          }
        }
        return response.data.data;
      }
      throw new Error(response.data.message || 'Dosya eklenemedi');
    } catch (error) {
      console.error('Dosya ekleme hatası:', error);
      console.error('Hata detayları:', error.response?.data);
      throw error;
    }
  }, [fetchTasks, selectedTask]);

  // Dosya sil
  const deleteAttachment = useCallback(async (taskId, attachmentId) => {
    try {
      const response = await api.delete(`/api/tasks/${taskId}/attachments/${attachmentId}`);
      
      if (response.data.success) {
        await fetchTasks();
        // Seçili görevi güncelle - response'dan gelen task bilgisini kullan
        if (selectedTask && selectedTask.id === taskId) {
          // Response'da task bilgisi varsa onu kullan
          if (response.data.data) {
            setSelectedTask(response.data.data);
          } else {
            // Yoksa API'den tekrar çek
            const taskResponse = await api.get(`/api/tasks/${taskId}`);
            if (taskResponse.data.success) {
              setSelectedTask(taskResponse.data.data);
            }
          }
        }
        return true;
      }
      throw new Error(response.data.message || 'Dosya silinemedi');
    } catch (error) {
      console.error('Dosya silme hatası:', error);
      throw error;
    }
  }, [fetchTasks, selectedTask]);

  // Dosya indir
  const downloadAttachment = useCallback(async (taskId, attachmentId, fileName) => {
    try {
      const response = await api.get(`/api/tasks/${taskId}/attachments/${attachmentId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Dosya indirme hatası:', error);
      throw error;
    }
  }, []);

  // Görev detayını aç
  const openTaskDetail = useCallback(async (taskId) => {
    try {
      const response = await api.get(`/api/tasks/${taskId}`);
      if (response.data.success) {
        setSelectedTask(response.data.data);
        setTaskDetailOpen(true);
      }
    } catch (error) {
      console.error('Görev detayı yükleme hatası:', error);
    }
  }, []);

  // Görev detayını kapat
  const closeTaskDetail = useCallback(() => {
    setSelectedTask(null);
    setTaskDetailOpen(false);
  }, []);

  const lastFetchAtRef = useRef(0);
  const TASK_POLL_MS = 3 * 60 * 1000; // 3 dakika
  const TASK_FOCUS_MIN_GAP_MS = 60 * 1000; // odak/görünür olunca en az 1 dk ara

  // Sayfa yüklendiğinde görevleri çek
  useEffect(() => {
    if (!user?.id) return;
    fetchTasks({ silent: false }).then(() => {
      lastFetchAtRef.current = Date.now();
    });
  }, [fetchTasks, user?.id]);

  // Periyodik + sekme odaklandığında güncelle (sık polling yok)
  useEffect(() => {
    if (!user?.id) return;

    const maybeRefresh = (forceGapMs = TASK_POLL_MS) => {
      if (document.visibilityState !== 'visible') return;
      if (fetchInProgressRef.current) return;
      if (Date.now() - lastFetchAtRef.current < forceGapMs) return;
      fetchTasks({ silent: true }).then(() => {
        lastFetchAtRef.current = Date.now();
      });
    };

    const interval = setInterval(() => maybeRefresh(TASK_POLL_MS), TASK_POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        maybeRefresh(TASK_FOCUS_MIN_GAP_MS);
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
  }, [fetchTasks, user?.id]);

  return (
    <TaskContext.Provider
      value={{
        tasks,
        loading,
        assignedToMe,
        assignedByMe,
        selectedTask,
        taskDetailOpen,
        fetchTasks,
        createTask,
        updateTask,
        completeTask,
        deleteTask,
        addComment,
        deleteComment,
        addAttachment,
        deleteAttachment,
        downloadAttachment,
        openTaskDetail,
        closeTaskDetail
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};

export const useTask = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error('useTask must be used within a TaskProvider');
  }
  return context;
};

export default TaskContext;

